/**
 * Base maps, overlays and the offline tile cache (docs/specs/map.md §7; inventory 05 F1, F2, F18).
 *
 * MB, Q66 (decision 93): keep today's providers as they are. The base maps are the Diurnal tool's five (Esri World
 * Imagery, a road map, OpenTopoMap, Esri imagery with Esri labels, Esri World Topo Map), and the reference overlays
 * its keyless ones. The road map ("Roads") is OpenStreetMap's standard tiles since 26 Oct 2026 (MB, decision 607):
 * CARTO Voyager, the Diurnal tool's, answered with an "API KEY REQUIRED" picture. Providers that need an access key
 * (OpenAIP aviation, OpenWeatherMap weather) are served only through Teslyn's provider proxy with the tenant's own
 * key (ADR-016): until the proxy is set up for the tenant they show as "Not set up" and are never called with a key
 * from the laptop.
 *
 * The upstream addresses live in the Rust core (`teslyn_core::map::tiles`); this module has what the screen shows
 * and the rules: limits, estimates and the hard cap of a pre-download.
 */

import { PROPERTY_LAYERS, type PropertyTileProvider } from './property-layers.js';

/** A tile provider of the cache (`teslyn_core::map::tiles::Provider`). */
export type TileProviderId =
  | 'esri-imagery'
  | 'esri-boundaries'
  | 'esri-transport'
  | 'esri-topo'
  | 'osm-standard'
  | 'opentopomap'
  | 'openrailwaymap'
  | 'waymarkedtrails-hiking'
  | 'openaip-chart'
  | 'owm-clouds'
  | 'owm-precipitation'
  | 'elev-arcgis-world'
  | PropertyTileProvider;

export interface TileProviderDef {
  readonly id: TileProviderId;
  readonly label: string;
  readonly attribution: string;
  /** The provider's copyright page, linked from the credit on a web map (OpenStreetMap asks for it). */
  readonly copyright_url?: string;
  /** Deepest zoom the provider serves (deeper tiles are scaled up). */
  readonly maxNativeZoom: number;
  /** Needs an access key: served only through the provider proxy (ADR-016). */
  readonly keyed: boolean;
  /** May be downloaded for offline use (weather tiles are time-sensitive and only cached as viewed). */
  readonly predownload: boolean;
  /**
   * Average tile size for estimates, bytes: measured on 18 Oct 2026 over the F26048 Milgun block (z10 to z16, eight
   * tiles each); keyed providers could not be measured without a key and use a nominal 4 kB (weather 8 kB).
   */
  readonly avgTileBytes: number;
}

const ESRI = 'Esri, Maxar, Earthstar Geographics';

export const TILE_PROVIDERS: readonly TileProviderDef[] = [
  {
    id: 'esri-imagery',
    label: 'Esri World Imagery',
    attribution: `© ${ESRI}`,
    maxNativeZoom: 17,
    keyed: false,
    predownload: true,
    avgTileBytes: 14_000,
  },
  {
    id: 'esri-boundaries',
    label: 'Esri boundaries and places',
    attribution: '© Esri',
    maxNativeZoom: 19,
    keyed: false,
    predownload: true,
    avgTileBytes: 1_000,
  },
  {
    id: 'esri-transport',
    label: 'Esri transportation',
    attribution: '© Esri',
    maxNativeZoom: 19,
    keyed: false,
    predownload: true,
    avgTileBytes: 1_000,
  },
  {
    id: 'esri-topo',
    label: 'Esri World Topo Map',
    attribution: '© Esri, HERE, Garmin, USGS, FAO, NPS, NRCAN, GeoBase, OpenStreetMap contributors',
    maxNativeZoom: 18,
    keyed: false,
    predownload: true,
    avgTileBytes: 8_000,
  },
  {
    // OpenStreetMap's tile usage policy (operations.osmfoundation.org/policies/tiles, read 26 Oct 2026): the credit
    // on the map, an app's own User-Agent, tiles cached (Teslyn Field keeps them 90 days), and no bulk or offline
    // download ("Offline use is not permitted on tile.openstreetmap.org"): cached as viewed, never pre-downloaded
    id: 'osm-standard',
    label: 'OpenStreetMap',
    attribution: '© OpenStreetMap contributors',
    copyright_url: 'https://www.openstreetmap.org/copyright',
    maxNativeZoom: 19,
    keyed: false,
    predownload: false,
    // the genuine tiles over Milgun on 26 Oct 2026: z10 1,527 bytes, z12 to z16 103 (empty country)
    avgTileBytes: 1_500,
  },
  {
    id: 'opentopomap',
    label: 'OpenTopoMap',
    attribution: '© OpenTopoMap (CC-BY-SA) © OpenStreetMap contributors',
    maxNativeZoom: 17,
    keyed: false,
    predownload: true,
    avgTileBytes: 10_000,
  },
  {
    id: 'openrailwaymap',
    label: 'OpenRailwayMap',
    attribution: 'Data © OpenStreetMap contributors, Style: CC-BY-SA 2.0 OpenRailwayMap',
    maxNativeZoom: 17,
    keyed: false,
    predownload: false,
    avgTileBytes: 200,
  },
  {
    id: 'waymarkedtrails-hiking',
    label: 'Waymarked Trails hiking',
    attribution: '© OpenStreetMap contributors, Waymarked Trails (CC BY-SA 3.0 DE)',
    maxNativeZoom: 17,
    keyed: false,
    // viewed and cached as viewed; Waymarked Trails strongly discourages mass downloads (docs/specs/map.md §19.6)
    predownload: false,
    avgTileBytes: 200,
  },
  {
    // OpenAIP's one raster layer since 2023 (airspace, airports, navaids, reporting points in one chart); the
    // genuine z9 tile over Perth is 13,481 bytes, z8 1,496 (20 Oct 2026)
    id: 'openaip-chart',
    label: 'OpenAIP aviation chart',
    attribution: 'Aviation data © openAIP (www.openaip.net), CC BY-NC 4.0',
    maxNativeZoom: 14,
    keyed: true,
    predownload: true,
    avgTileBytes: 7_000,
  },
  {
    id: 'owm-clouds',
    label: 'OpenWeatherMap clouds',
    attribution: '© OpenWeatherMap',
    maxNativeZoom: 19,
    keyed: true,
    predownload: false,
    avgTileBytes: 8_000,
  },
  {
    id: 'owm-precipitation',
    label: 'OpenWeatherMap precipitation',
    attribution: '© OpenWeatherMap',
    maxNativeZoom: 19,
    keyed: true,
    predownload: false,
    avgTileBytes: 8_000,
  },
  // the 3D view's fallback only (decisions 648, 649: Copernicus or DEM-S read by the core, online, never downloaded)
  {
    id: 'elev-arcgis-world',
    label: 'Esri World Elevation (3D view, when Copernicus cannot be reached)',
    attribution: '© Esri and its data providers',
    maxNativeZoom: 16,
    keyed: false,
    predownload: false,
    avgTileBytes: 46_000,
  },
  // the state property layers (Diurnal parity 04-021, decision 619; `property-layers.ts`): cached as viewed, never
  // pre-downloaded (NSW's terms limit automated retrieval); the NT tile of the fixture is 28,661 bytes, the others a
  // nominal 8 kB
  ...PROPERTY_LAYERS.map((l): TileProviderDef => ({
    id: l.provider,
    label: l.label,
    attribution: l.attribution,
    maxNativeZoom: 19,
    keyed: false,
    predownload: false,
    avgTileBytes: l.id === 'nt' ? 28_661 : 8_000,
  })),
];

export const providerDef = (id: TileProviderId): TileProviderDef => TILE_PROVIDERS.find((p) => p.id === id)!;

export type BaseLayerId = 'satellite' | 'roads' | 'terrain' | 'hybrid' | 'topo';

export interface BaseLayerDef {
  readonly id: BaseLayerId;
  readonly label: string;
  /** Tiles drawn bottom first (hybrid: imagery then labels). */
  readonly providers: readonly TileProviderId[];
  readonly maxZoom: number;
}

/**
 * The five base maps (inventory 05 F1). `topo` is Esri World Topo Map through the cache too (the Diurnal proxy sent
 * it to OpenTopoMap by mistake, am:4836).
 */
export const BASE_LAYERS: readonly BaseLayerDef[] = [
  { id: 'satellite', label: 'Satellite', providers: ['esri-imagery'], maxZoom: 20 },
  {
    id: 'hybrid',
    label: 'Satellite with labels',
    providers: ['esri-imagery', 'esri-boundaries'],
    maxZoom: 20,
  },
  { id: 'roads', label: 'Roads', providers: ['osm-standard'], maxZoom: 20 },
  { id: 'terrain', label: 'Terrain', providers: ['opentopomap'], maxZoom: 17 },
  { id: 'topo', label: 'Topographic', providers: ['esri-topo'], maxZoom: 18 },
];

export type OverlayId =
  | 'roads'
  | 'place_names'
  | 'railways'
  | 'hiking'
  | 'aviation_chart'
  | 'weather_clouds'
  | 'weather_precipitation';

export interface OverlayDef {
  readonly id: OverlayId;
  readonly label: string;
  readonly group: 'Map detail' | 'Aviation' | 'Weather';
  readonly provider: TileProviderId;
}

/** The tile overlays (inventory 05 F2), grouped as the layer panel shows them. */
export const OVERLAYS: readonly OverlayDef[] = [
  { id: 'roads', label: 'Roads and highways', group: 'Map detail', provider: 'esri-transport' },
  {
    id: 'place_names',
    label: 'Place names and boundaries',
    group: 'Map detail',
    provider: 'esri-boundaries',
  },
  { id: 'railways', label: 'Railways', group: 'Map detail', provider: 'openrailwaymap' },
  { id: 'hiking', label: 'Hiking trails', group: 'Map detail', provider: 'waymarkedtrails-hiking' },
  { id: 'aviation_chart', label: 'OpenAIP chart (tiles)', group: 'Aviation', provider: 'openaip-chart' },
  { id: 'weather_clouds', label: 'Clouds', group: 'Weather', provider: 'owm-clouds' },
  { id: 'weather_precipitation', label: 'Precipitation', group: 'Weather', provider: 'owm-precipitation' },
];

/**
 * Whether a provider can be used now: keyless providers unless their terms rule them out; keyed ones only when the
 * organisation has set up the provider's key behind the provider proxy (ADR-016, `keyedTileProviders`).
 */
export function providerAvailable(p: TileProviderDef, proxyProviders: ReadonlySet<TileProviderId>): boolean {
  if (p.id in UNAVAILABLE_PROVIDERS) return false;
  return !p.keyed || proxyProviders.has(p.id);
}

/**
 * The keyed tile providers an organisation can use, from the Integrations providers it has set up (ADR-016): OpenAIP's
 * chart, OpenWeatherMap's clouds and precipitation. They are fetched through the provider proxy with the user's
 * sign-in (docs/specs/map.md §19.2); the key never reaches the laptop.
 */
export function keyedTileProviders(setUp: ReadonlySet<string>): Set<TileProviderId> {
  const out = new Set<TileProviderId>();
  if (setUp.has('openaip')) out.add('openaip-chart');
  if (setUp.has('openweathermap')) {
    out.add('owm-clouds');
    out.add('owm-precipitation');
  }
  return out;
}

/** Providers that cannot be used for a reason other than a key. */
export const UNAVAILABLE_PROVIDERS: Readonly<Partial<Record<TileProviderId, string>>> = {
  // docs/specs/map.md §19.6: OpenRailwayMap's tile terms ("If you use the tiles or the API in a for-profit context,
  // set up your own servers"; "Applications … have to be open to the public (free of charge and registration)")
  openrailwaymap:
    'Not offered: OpenRailwayMap allows its tiles only in free public applications, and Teslyn is a commercial application.',
};

/** Why a provider cannot be used, or null. */
export function unavailableReason(
  p: TileProviderDef,
  proxyProviders: ReadonlySet<TileProviderId>,
): string | null {
  if (providerAvailable(p, proxyProviders)) return null;
  return UNAVAILABLE_PROVIDERS[p.id] ?? NOT_SET_UP;
}

export const NOT_SET_UP =
  "Not set up: this layer needs your organisation's own access key, which a tenant admin adds in Teslyn Cloud (Integrations).";

// Cache limits (Settings; today's defaults, inventory 05 F18)

export interface TileCacheSettings {
  /** A cached tile older than this is refreshed when online (and still shown when offline). */
  readonly max_age_days: number;
  /** The cache is trimmed to this size, least recently used first. */
  readonly max_size_gb: number;
  /** Deepest zoom a pre-download fetches. */
  readonly max_zoom: number;
}

export const DEFAULT_TILE_CACHE_SETTINGS: TileCacheSettings = {
  max_age_days: 90,
  max_size_gb: 100,
  max_zoom: 17,
};

export const TILE_CACHE_LIMITS = {
  max_age_days: [7, 365],
  max_size_gb: [1, 2000],
  max_zoom: [8, 19],
} as const;

export function validateTileCacheSettings(s: TileCacheSettings): string[] {
  const out: string[] = [];
  const check = (k: keyof TileCacheSettings, label: string, unit: string): void => {
    const [lo, hi] = TILE_CACHE_LIMITS[k];
    const v = s[k];
    if (!Number.isInteger(v) || v < lo || v > hi)
      out.push(`${label} must be a whole number from ${lo}${unit} to ${hi}${unit}`);
  };
  check('max_age_days', 'Keep tiles for', ' days');
  check('max_size_gb', 'Cache size', ' GB');
  check('max_zoom', 'Deepest zoom', '');
  return out;
}

/** The shallowest zoom a pre-download fetches (overview tiles come first, inventory 05 F18). */
export const PREDOWNLOAD_MIN_ZOOM = 8;

/** The most tiles one pre-download may queue (the Diurnal tool had no cap, inventory 05 F18 "Limits and risks"). */
export const PREDOWNLOAD_HARD_CAP = 500_000;

/** Providers fetched for a project area by default (today's: imagery and labels; OpenAIP once set up). */
export const DEFAULT_PREDOWNLOAD_PROVIDERS: readonly TileProviderId[] = [
  'esri-imagery',
  'esri-boundaries',
  'openaip-chart',
];

/** A nominal download rate for the estimate before a download has started (tiles a second). */
export const NOMINAL_TILES_PER_SECOND = 50;

/** Tile counts per provider, as `tile_estimate` returns them. */
export interface TileEstimate {
  readonly providers: ReadonlyArray<{
    readonly provider: TileProviderId;
    readonly total: number;
    readonly cached: number;
  }>;
  readonly min_zoom: number;
  readonly max_zoom: number;
}

export interface EstimateSummary {
  readonly toFetch: number;
  readonly bytes: number;
  readonly seconds: number;
  readonly overCap: boolean;
  /** "12,345 tiles to fetch, about 290 MB, about 4 min at 50 tiles a second". */
  readonly text: string;
}

const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-AU');

function fmtBytes(b: number): string {
  if (b >= 1e9) return `${(b / 1e9).toLocaleString('en-AU', { maximumFractionDigits: 1 })} GB`;
  return `${fmtInt(Math.max(1, b / 1e6))} MB`;
}

function fmtDuration(s: number): string {
  if (s < 90) return `${fmtInt(Math.max(1, s))} s`;
  if (s < 5400) return `${fmtInt(s / 60)} min`;
  return `${(s / 3600).toLocaleString('en-AU', { maximumFractionDigits: 1 })} h`;
}

/** What a pre-download would fetch, in words, with the hard cap applied. */
export function summariseEstimate(
  e: TileEstimate,
  tilesPerSecond = NOMINAL_TILES_PER_SECOND,
): EstimateSummary {
  let toFetch = 0;
  let bytes = 0;
  for (const p of e.providers) {
    const missing = Math.max(0, p.total - p.cached);
    toFetch += missing;
    bytes += missing * providerDef(p.provider).avgTileBytes;
  }
  const seconds = toFetch / Math.max(1, tilesPerSecond);
  const overCap = toFetch > PREDOWNLOAD_HARD_CAP;
  const text = overCap
    ? `${fmtInt(toFetch)} tiles to fetch is more than the limit of ${fmtInt(PREDOWNLOAD_HARD_CAP)}. Choose a lower deepest zoom or a smaller area.`
    : toFetch === 0
      ? 'Every tile of this area is already cached.'
      : `${fmtInt(toFetch)} tiles to fetch, about ${fmtBytes(bytes)}, about ${fmtDuration(seconds)} at ${fmtInt(tilesPerSecond)} tiles a second`;
  return { toFetch, bytes, seconds, overCap, text };
}

/** Download progress (`tile-download-progress` event and `tile_download_status`). */
export interface TileDownloadProgress {
  readonly state: 'idle' | 'running' | 'paused' | 'done' | 'failed';
  readonly completed: number;
  readonly total: number;
  readonly failed: number;
  readonly unavailable: number;
  readonly retries: number;
  readonly message: string;
}

export function progressText(p: TileDownloadProgress): string {
  const pct = p.total > 0 ? Math.floor((p.completed / p.total) * 100) : 0;
  const tail = [
    p.failed > 0 ? `${fmtInt(p.failed)} failed` : '',
    p.unavailable > 0 ? `${fmtInt(p.unavailable)} not available from the provider` : '',
  ]
    .filter(Boolean)
    .join(', ');
  const head =
    p.state === 'idle'
      ? 'No download running'
      : p.state === 'paused'
        ? `Paused at ${fmtInt(p.completed)} of ${fmtInt(p.total)} tiles (${pct} %)`
        : p.state === 'done'
          ? `Done: ${fmtInt(p.completed)} of ${fmtInt(p.total)} tiles`
          : p.state === 'failed'
            ? `Stopped: ${p.message}`
            : `Downloading ${fmtInt(p.completed)} of ${fmtInt(p.total)} tiles (${pct} %)`;
  return tail ? `${head}; ${tail}` : head;
}
